import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { openExternalUrl } from '../../utils/openExternalUrl';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { authHrefReturningTo } from '../../utils/postAuthReturn';
import { trackEvent } from '../../services/analyticsService';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';

/** `sign-in`: the source fetch needs an account and the reader is a guest (#775). */
export type ScrapeErrorType = 'blocked' | 'network' | 'not-found' | 'sign-in';

interface RecipeDataErrorCardProps {
  errorType: ScrapeErrorType;
  section: 'ingredients' | 'steps';
  onRetry?: () => void;
  sourceUrl?: string;
  onEditManually: () => void;
  /** Where signing up comes back to (this recipe), for `sign-in`. */
  returnTo?: string;
}

// Copy lives in the locale files — this card is the failure state a French user
// actually sees when a recipe won't load (#565).
const ERROR_CONFIG: Record<ScrapeErrorType, {
  icon: keyof typeof Feather.glyphMap;
  key: string;
  showRetry: boolean;
  showOpenSource: boolean;
}> = {
  blocked: {
    icon: 'lock',
    key: 'blocked',
    showRetry: false,
    showOpenSource: true,
  },
  network: {
    icon: 'wifi-off',
    key: 'network',
    showRetry: true,
    showOpenSource: false,
  },
  'not-found': {
    icon: 'search',
    key: 'notFound',
    showRetry: true,
    showOpenSource: true,
  },
  'sign-in': {
    icon: 'user',
    key: 'signIn',
    showRetry: false,
    showOpenSource: false,
  },
};

export const RecipeDataErrorCard: React.FC<RecipeDataErrorCardProps> = ({
  errorType,
  section,
  onRetry,
  sourceUrl,
  onEditManually,
  returnTo,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const config = ERROR_CONFIG[errorType];
  const sectionLabel = t(`recipeDetail.dataError.section.${section}`);

  return (
    <View style={styles.card}>
      <Feather name={config.icon} size={20} color={Paprika.textMuted} style={styles.icon} />
      <Text style={styles.title}>{t(`recipeDetail.dataError.${config.key}.title`)}</Text>
      <Text style={styles.body}>
        {t(`recipeDetail.dataError.${config.key}.body`, { section: sectionLabel })}
      </Text>
      <View style={styles.actions}>
        {errorType === 'sign-in' && (
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={() => {
              trackEvent('guest_sign_in_clicked', { source: 'recipe_detail', section });
              router.push(authHrefReturningTo(returnTo) as any);
            }}
            testID="recipe-data-sign-in"
          >
            <Text style={styles.primaryBtnText}>{t('scanGate.cta')}</Text>
          </TouchableOpacity>
        )}
        {config.showRetry && onRetry && (
          <TouchableOpacity style={styles.primaryBtn} onPress={onRetry}>
            <Text style={styles.primaryBtnText}>{t('common.retry')}</Text>
          </TouchableOpacity>
        )}
        {/* `!!`: an empty sourceUrl rendered '' inside this View (#791). */}
        {config.showOpenSource && !!sourceUrl && (
          <TouchableOpacity style={styles.primaryBtn} onPress={() => void openExternalUrl(sourceUrl)}>
            <Text style={styles.primaryBtnText}>{t('recipeDetail.dataError.openSource')}</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity style={styles.secondaryBtn} onPress={onEditManually}>
          <Text style={styles.secondaryBtnText}>{t('recipeDetail.dataError.editManually')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    margin: PaprikaSpacing.lg,
    padding: PaprikaSpacing.lg,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    gap: PaprikaSpacing.xs,
  },
  icon: {
    marginBottom: PaprikaSpacing.xs,
  },
  title: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textSecondary,
  },
  body: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    lineHeight: 20,
    marginBottom: PaprikaSpacing.sm,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.xs,
  },
  primaryBtn: {
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.full,
  },
  primaryBtnText: {
    ...PaprikaTypography.labelMedium,
    color: '#fff',
  },
  secondaryBtn: {
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: PaprikaRadius.full,
  },
  secondaryBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
});
